/**
 * The shell's load order, which is also the start gate's readiness condition.
 *
 * It runs in three steps rather than one because each unblocks something the
 * player can see: the fonts let the gate draw its prompt, the artwork lets the
 * Main Menu be drawn behind the gate (and *is* the menu, on a revisit), and
 * only when the songs and stings have buffered too is the intro allowed to
 * tick. The preload set itself is derived from the tick list, so the wait is
 * for exactly what the script will reach for and nothing else.
 */

import type { ShellAudio } from "./audio/shell-audio.ts";
import { introPreloadSet } from "./intro/intro-cues.ts";
import { loadIntroSource } from "./intro/intro-source.ts";
import type { IntroTick } from "./intro/intro-ticks.ts";
import { MAIN_MENU_BACKGROUND, ORNAMENT_SHEET } from "./render/menu-chrome.ts";
import { loadShellArtwork, loadShellFonts, type ShellAssets } from "./render/shell-assets.ts";

export interface ShellBootSteps {
  /** Palette, manifest and the three atlases are in: the gate can draw its prompt. */
  onFonts(assets: ShellAssets): void;
  /** Every screen and sheet is decoded: the Main Menu can be painted while the audio still loads. */
  onArtwork(assets: ShellAssets): void;
}

export interface ShellBoot {
  readonly assets: ShellAssets;
  readonly ticks: readonly IntroTick[];
}

export async function bootShell(audio: ShellAudio, steps: ShellBootSteps): Promise<ShellBoot> {
  const fonts = await loadShellFonts();
  steps.onFonts(fonts);

  const ticks = (await loadIntroSource(fonts.manifest)).ticks;
  const preload = introPreloadSet(ticks);
  const assets = await loadShellArtwork(fonts, {
    images: [MAIN_MENU_BACKGROUND, ...preload.images],
    sprites: [ORNAMENT_SHEET, ...preload.sprites],
  });
  steps.onArtwork(assets);

  await audio.preload(preload.audio);
  return { assets, ticks };
}
